import React, { memo, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  Typography,
  IconButton,
  TextField,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Menu,
  ListItemText, InputAdornment,
} from "@material-ui/core";
import {
  Add as AddIcon,
  GetApp as ExportIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from "@material-ui/icons";
import { StyledTableCell, StyledTableRow } from "../../../../../components/table";
import { TablePaginationActions } from "../../../../../components/tablePagination";
import moment from "moment-timezone";
import { getLang } from "../../../../../app/feature/constants";
import {Formik} from "formik";
import * as Yup from "yup";
import Skeleton from "@material-ui/lab/Skeleton";
import {DateRangePicker} from "rsuite";
import 'rsuite/dist/rsuite.min.css';

const VoucherTable = memo(({
  classes,
  lang,
  listing,
  totalRecords,
  isFetching,
  page,
  rowsPerPage,
  totalAvailable,
  totalIssued,
  timezone,
  handleChangePage,
  handleChangeRowsPerPage,
  setIsAdding,
  handleExport,
  handleResetFilters,
  handleSearch,
  filter,
  handleClickDelete,
  canAddVoucher,
  canEditVoucher,
  canDeleteVoucher,
}) => {
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  return (
    <Box>
      {/* Header with Actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2">
          {getLang(lang, "label.VOUCHER_CODE_LISTING")}
        </Typography>
        <Box className={classes.actionButtons}>
          {canAddVoucher && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setIsAdding(true)}
            >
              {getLang(lang, "label.IMPORT_VOUCHER_CODES")}
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ExportIcon />}
            onClick={handleExportClick}
          >
            {getLang(lang, "label.EXPORT")}
          </Button>
        </Box>
      </Box>

      {/* Export Format Menu */}
      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => {
          handleExport('csv');
          handleExportClose();
        }}>
          <ListItemText primary="CSV"/>
        </MenuItem>
        <MenuItem onClick={() => {
          handleExport('xlsx');
          handleExportClose();
        }}>
          <ListItemText primary="XLSX" />
        </MenuItem>
      </Menu>

      {/* Filter Section */}
      <Formik
        initialValues={filter}
        validationSchema={Yup.object({
          searchText: Yup.string(),
          startDate: Yup.date().nullable(),
          endDate: Yup.date()
            .nullable()
            .when("startDate", {
              is: (startDate) => startDate,
              then: Yup.date().min(
                Yup.ref("startDate"),
                getLang(lang, "message.error.END_DATE_MUST_BE_AFTER_START_DATE")
              ),
            }),
          status: Yup.string(),
        })}
        onSubmit={handleSearch}
      >
        {formik => (
          <form onSubmit={formik.handleSubmit}>
            <Box style={{ marginBottom: 16 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Grid container spacing={0}>
                    {/* Select (4/12) */}
                    <Grid item xs={4}>
                      <TextField
                        select
                        variant="outlined"
                        size="small"
                        fullWidth
                        name="searchBy"
                        value={formik.values.searchBy}
                        onChange={(e) => {
                          // optional: reset searchText when changing searchBy
                          formik.setFieldValue("searchBy", e.target.value);
                          formik.setFieldValue("searchText", "");
                        }}
                        className={classes.selectWrapper}
                      >
                        <MenuItem value="voucher_code">
                          {getLang(lang, "label.VOUCHER_CODE")}
                        </MenuItem>
                        <MenuItem value="issued_to">
                          {getLang(lang, "label.ISSUED_TO")}
                        </MenuItem>
                      </TextField>
                    </Grid>

                    {/* Text Field (8/12) */}
                    <Grid item xs={8}>
                      <TextField
                        name="searchText"
                        variant="outlined"
                        size="small"
                        fullWidth
                        className={classes.textWrapper}
                        placeholder={{
                          voucher_code: getLang(lang, "placeholder.SEARCH_VOUCHER_CODE"),
                          issued_to: getLang(lang, "placeholder.SEARCH_ISSUED_TO"),
                        }[formik.values.searchBy] ?? getLang(lang, "placeholder.SEARCH")}
                        value={formik.values.searchText}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Status Dropdown */}
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <Select
                      placeholder={getLang(lang, "label.STATUS")}
                      name="status"
                      value={formik.values.status}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      {[
                        { value: -1, label: getLang(lang, "label.ALL_STATUS") },
                        { value: 0, label: getLang(lang, "label.AVAILABLE") },
                        { value: 1, label: getLang(lang, "label.ISSUED") },
                        { value: 2, label: getLang(lang, "label.DELETED") },
                      ].map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Start Date */}
                <Grid item xs={12} md={3} className={classes.datePicker}>
                  <DateRangePicker
                    character=" - "
                    cleanable={false}
                    placeholder={getLang(lang, "placeholder.SELECT_DATE_RANGE")}
                    format="dd MMM yyyy"
                    placement="auto"
                    disabledDate={(date) => date.getTime() > Date.now()}
                    editable={false}
                    size="lg"
                    block
                    onChange={(value) => {
                      formik.setFieldValue('startDate', value[0] ? moment(value[0]).startOf('day').toDate() : null);
                      formik.setFieldValue('endDate', value[1] ? moment(value[1]).endOf('day').toDate() : null);
                    }}
                    value={formik.values.startDate && formik.values.endDate
                      ? [formik.values.startDate, formik.values.endDate]
                      : null}
                    ranges={[]}
                  />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    formik.handleReset();
                    formik.handleSubmit();
                  }}
                  fullWidth
                  size="small"
                >
                  {getLang(lang, "label.RESET")}
                </Button>
                <Box ml={1} />
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<SearchIcon />}
                  type="submit"
                  fullWidth
                  size="small"
                >
                  {getLang(lang, "label.SEARCH")}
                </Button>
              </Box>
            </Box>
          </form>
        )}
      </Formik>

      <Box className={classes.summaryContainer} mt={1}>
        <Box className={classes.card}>
          <Box>
            <Typography variant="body1" style={{ color: "#7f7f7f" }}>
              {getLang(lang, "label.TOTAL_AVAILABLE")}
            </Typography>
          </Box>
          <Box mt={0.5} mb={1} ml={0.5} display="flex" alignItems="flex-end">
            {isFetching ? (
              <Skeleton variant="text" animation="wave" width="100%" height={30} />
            ) : (
              <Typography variant="h5" style={{ marginRight: 4 }}>
                <b>{totalAvailable?.toLocaleString("en-US") || 0}</b>
              </Typography>
            )}
          </Box>
        </Box>
        <Box className={classes.card}>
          <Box>
            <Typography variant="body1" style={{ color: "#7f7f7f" }}>
              {getLang(lang, "label.TOTAL_ISSUED")}
            </Typography>
          </Box>
          <Box mt={0.5} mb={1} ml={0.5} display="flex" alignItems="flex-end">
            {isFetching ? (
              <Skeleton variant="text" animation="wave" width="100%" height={30} />
            ) : (
              <Typography variant="h5" style={{ marginRight: 4 }}>
                <b>{totalIssued?.toLocaleString("en-US") || 0}</b>
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Table */}
      <Box style={{ border: "1px solid rgba(58, 77, 84, 0.1)", borderRadius: 4, overflow: "hidden" }}>
        <TableContainer>
          <Table className={classes.table} size="small" stickyHeader>
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>
                  {getLang(lang, "label.VOUCHER_CODE")}
                </StyledTableCell>
                <StyledTableCell>
                  {getLang(lang, "label.STATUS")}
                </StyledTableCell>
                <StyledTableCell>
                  {getLang(lang, "label.ISSUED_TO")}
                </StyledTableCell>
                <StyledTableCell>
                  {getLang(lang, "label.ISSUED_AT")}
                </StyledTableCell>
                {canDeleteVoucher && (
                  <StyledTableCell align="center">
                    {getLang(lang, "label.ACTION")}
                  </StyledTableCell>
                )}
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {!isFetching && listing && listing.length > 0 ? (
                listing.map((item, index) => {
                  return (
                    <StyledTableRow
                      key={index}
                      hover
                      role="checkbox"
                    >
                      <StyledTableCell>
                        {item.code || `VOUCHER-${index + 1}`}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography
                          style={{
                            fontSize: ".75rem",
                            color: [
                              "green",
                              "orange",
                              "red",
                              "gray"
                            ][item.status] ?? 'gray',
                          }}
                        >
                          {[
                            getLang(lang, "label.AVAILABLE"),
                            getLang(lang, "label.ISSUED"),
                            getLang(lang, "label.DELETED"),
                          ][item.status] || getLang(lang, "label.DELETED")}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        {item.issued_to || '-'}
                      </StyledTableCell>
                      <StyledTableCell>
                        {item.issued_at ? moment(item.issued_at).tz(timezone).format("YYYY-MM-DD HH:mm") : '-'}
                      </StyledTableCell>
                      {canDeleteVoucher && (
                        <StyledTableCell align="center">
                          <IconButton
                            color="secondary"
                            size="small"
                            disabled={item.status !== 0}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClickDelete(item.uuid);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </StyledTableCell>
                      )}
                    </StyledTableRow>
                  );
                })
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={5} align="center">
                    <Typography color="textSecondary">
                      {isFetching ? getLang(lang, "label.LOADING") : getLang(lang, "label.NO_DATA_AVAILABLE")}
                    </Typography>
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!isFetching && listing && listing.length > 0 && (
          <TablePagination
            style={{
              backgroundColor: "#fff",
              boxShadow: "0px -1px 0px rgba(58, 77, 84, 0.15)",
            }}
            ActionsComponent={TablePaginationActions}
            rowsPerPageOptions={[50, 100, 200, 500]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            labelRowsPerPage={getLang(lang, 'label.ROWS_PER_PAGE')}
            labelDisplayedRows={({ from, to, count }) =>
              `${from.toLocaleString("en-US")}-${to.toLocaleString("en-US")} ${getLang(lang, 'label.OF')} ${count.toLocaleString("en-US")}`}
          />
        )}
      </Box>
    </Box>
  );
});

export default VoucherTable;
